import React, {
  memo,
  useState,
  useCallback,
  useMemo,
  useEffect,
  useContext,
} from "react";
import styled, { DefaultTheme, useTheme } from "styled-components/native";
import { View } from "react-native";
import { VDBText } from "@components/atoms";
import { SpaceType } from "@components/atoms/Spacer/types";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { SelectedItemTheme } from "@components/atoms/Button/types";
import {
  setFiltersPayload,
  useSearchFiltersSlice,
} from "@redux/slices/searchFilters";
import { useDispatch } from "react-redux";
import { FilterScreenContext } from "@scenes/explore/FilterScreenContext";
import useColorFilterSelection from "../FilterHooks/UseColorFilterSelection";
import { SearchFilterNameEnum } from "../utils";
import { extractKeyValuePairs, getOrder, hasAllFilters } from "./utils";
import { useTranslation } from "react-i18next";

type TabsSectionProps = {
  sub_filters: Array<{ name: string; label: string }>;
  children: React.ReactNode[];
  selectedStyle: SelectedItemTheme;
  sectionName: string;
};

type TabProps = {
  name: string;
  isActive: boolean;
  onPress: () => void;
  selectedStyle: SelectedItemTheme;
  typography: any;
  theme: DefaultTheme;
};

const Tab: React.FC<TabProps> = memo(
  ({ name, isActive, onPress, selectedStyle, typography, theme }) => (
    <TabButton
      onPress={onPress}
      isActive={isActive}
      selectedStyle={selectedStyle}
    >
      <VDBText
        type={typography["H3-M"]}
        color={
          isActive
            ? (theme[selectedStyle.secondary as keyof DefaultTheme] as string)
            : theme["core-06"]
        }
      >
        {name}
      </VDBText>
    </TabButton>
  )
);

const TabsSection: React.FC<TabsSectionProps> = memo(
  ({ sub_filters = [], children = [], selectedStyle, sectionName = "" }) => {
    const [activeTabIndex, setActiveTabIndex] = useState(0);
    const typography = useTypographyStore();
    const theme = useTheme();
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const {
      isFancyColorsSelected,
      isOvertonesSelected,
      isIntensitiesSelected,
      isWhiteColorsSelected,
    } = useColorFilterSelection(sectionName);
    const { auxPayload: filtersPayload } = useSearchFiltersSlice();

    const { containerWidth } = useContext(FilterScreenContext);

    const handleTabPress = useCallback((index: number) => {
      setActiveTabIndex(index);
      setTotalPriceParam(index);
    }, []);

    const setTotalPriceParam = useCallback(
      (index: number) => {
        if (["Total Price", "P/ct"].includes(sub_filters?.[index]?.name)) {
          const payload: any = {};
          const payloadKey = "total_or_price_per_carat";
          const payloadValue =
            sub_filters[index].name === "Total Price"
              ? "total_sales_price"
              : "price_per_carat";
          payload[payloadKey] = payloadValue;
          if (sectionName) {
            dispatch(setFiltersPayload({ sectionName, data: payload }));
          }
        }
      },
      [dispatch, sectionName, sub_filters]
    );

    useEffect(() => {
      setTotalPriceParam(activeTabIndex);
    }, [activeTabIndex, setTotalPriceParam]);

    useEffect(() => {
      setTotalPriceParam(0);
    }, [setTotalPriceParam]);

    useEffect(() => {
      const handleSelectedColorTab = () => {
        const selectedColor =
          isFancyColorsSelected() ||
          isIntensitiesSelected() ||
          isOvertonesSelected()
            ? SearchFilterNameEnum.Fancy
            : isWhiteColorsSelected()
            ? SearchFilterNameEnum.White
            : null;

        if (selectedColor) {
          handleTabPress(getOrder(selectedColor, sub_filters));
        }
      };

      const handlePriceTab = () => {
        sub_filters.forEach((item: any) => {
          if (Object.keys(filtersPayload).length > 0) {
            const sub_filter = extractKeyValuePairs(
              filtersPayload?.[sectionName],
              item?.payload_name
            );
            if (Object.keys(sub_filter).length > 0) {
              handleTabPress(getOrder(item?.name, sub_filters));
            }
          }
        });
      };

      if (
        hasAllFilters(
          [SearchFilterNameEnum.Fancy, SearchFilterNameEnum.White],
          sub_filters
        )
      ) {
        handleSelectedColorTab();
      } else if (
        hasAllFilters(
          [
            SearchFilterNameEnum.TOTAL_PRICE,
            SearchFilterNameEnum.PRICE_PER_CARAT,
          ],
          sub_filters
        )
      ) {
        handlePriceTab();
      }
    }, [sub_filters, sectionName]);

    const tabs = useMemo(
      () =>
        sub_filters?.map((sub_filter, index) => (
          <Tab
            key={index}
            name={
              sub_filter?.label !== ""
                ? t(sub_filter?.label)
                : t(sub_filter?.name)
            }
            isActive={index === activeTabIndex}
            onPress={() => handleTabPress(index)}
            selectedStyle={selectedStyle}
            typography={typography}
            theme={theme}
          />
        )),
      [
        sub_filters,
        activeTabIndex,
        handleTabPress,
        selectedStyle,
        typography,
        theme,
      ]
    );

    return (
      <>
        <TabsContainer horizontal={true}>{tabs}</TabsContainer>
        <Separator width={theme.isLargeScreen ? "auto" : containerWidth} />
        <View>{children?.[activeTabIndex]}</View>
      </>
    );
  }
);

const TabButton = styled.TouchableOpacity<{
  isActive: boolean;
  selectedStyle: SelectedItemTheme;
}>`
  border-bottom-width: 1px;
  border-color: ${({
    theme,
    isActive,
    selectedStyle,
  }: {
    theme: DefaultTheme;
    isActive: boolean;
    selectedStyle: SelectedItemTheme;
  }) =>
    isActive
      ? (theme[selectedStyle.secondary as keyof DefaultTheme] as string)
      : "transparent"};
  margin-right: ${SpaceType.Double}px;
  padding-top: ${SpaceType.Extra}px;
  padding-bottom: ${SpaceType.Extra}px;
`;

const Separator = styled.View<{ width: number }>`
  height: 1px;
  width: ${({ width }: { width: number }) => width}px;
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-03"]};
  margin-bottom: ${SpaceType.Double}px;
  margin-top: -1px;
  z-index: -1;
`;

const TabsContainer = styled.ScrollView`
  padding-left: ${SpaceType.Double}px;
`;

export default TabsSection;
